import { useState, useEffect } from 'react';
import * as fmt from '../../i18n/format';
import { useCopy, fill } from '../../i18n';
import { CalendarClock, CheckCheck, Loader2 } from 'lucide-react';
import { notificationService } from '../../services/notificationService';
import type { Notification } from '../../types/database';

interface NotificationDropdownProps {
  /**
   * Ocupa a largura toda, para quando esta lista e a TELA e nao o popover do
   * sino. Sem isto o `w-80` de popover fica no meio de uma tela de 375px, e a
   * borda do cabecalho corta pela metade.
   */
  fullWidth?: boolean;
  onNavigateToTask?: (projectId: string, taskId: string) => void;
  onClose: () => void;
}

export function NotificationDropdown({ onNavigateToTask, onClose, fullWidth = false }: NotificationDropdownProps) {
  const t = useCopy().shell.notifications;
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    try {
      const data = await notificationService.getRecent(30);
      setNotifications(data);
    } catch (err) {
      console.error('Error loading notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (notification: Notification) => {
    if (!notification.read_at) {
      await notificationService.markRead(notification.id);
      setNotifications(prev =>
        prev.map(n => n.id === notification.id ? { ...n, read_at: new Date().toISOString() } : n)
      );
    }
    if (notification.project_id && notification.task_id && onNavigateToTask) {
      onNavigateToTask(notification.project_id, notification.task_id);
      onClose();
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationService.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
    } catch (err) {
      console.error('Error marking all read:', err);
    }
  };

  const formatRelativeTime = (dateStr: string): string => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffMin < 1) return 'agora';
    if (diffMin < 60) return `${diffMin}min`;
    if (diffHour < 24) return `${diffHour}h`;
    if (diffDay < 7) return `${diffDay}d`;
    return fmt.date(date, { day: '2-digit', month: '2-digit' });
  };

  const getInitial = (name: string | null) => {
    return (name || '?').charAt(0).toUpperCase();
  };

  const getAvatarColor = (id: string) => {
    const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4'];
    let hash = 0;
    for (let i = 0; i < id.length; i++) hash = id.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
  };

  /**
   * Renderiza o template destacando o alvo. O slot `{{target}}` existe em vez
   * de concatenacao porque a POSICAO dele muda com o idioma: em portugues o
   * prazo vem antes do "esta proximo", em ingles vem depois do verbo.
   */
  const renderMessage = (template: string, target: string) => {
    const [antes, depois = ''] = template.split('{{target}}');
    return (
      <>
        {antes}
        {template.includes('{{target}}') && <span className="font-semibold">{target}</span>}
        {depois}
      </>
    );
  };

  const getNotificationMessage = (notification: Notification) => {
    const taskName = notification.data?.task_name || t.fallbackTask;
    const projectName = notification.data?.project_name || t.fallbackProject;
    switch (notification.type) {
      case 'mention':
        return renderMessage(t.mention, taskName);
      case 'comment':
        return renderMessage(t.comment, taskName);
      case 'task_assigned':
        return renderMessage(t.taskAssigned, taskName);
      case 'task_unassigned':
        return renderMessage(t.taskUnassigned, taskName);
      case 'task_status':
        return renderMessage(t.taskStatus, taskName);
      case 'task_deadline':
        // Sem ator: a frase e uma sentenca completa, comeca com maiuscula.
        // `state` distingue atraso de vencimento, que sao dois controles
        // diferentes na tela de preferencias e duas urgencias diferentes.
        return renderMessage(
          notification.data?.state === 'overdue' ? t.deadlineOverdue : t.deadlineSoon,
          taskName
        );
      case 'project_invite':
        return renderMessage(t.projectInvite, projectName);
      case 'capacity_overload': {
        // O tipo era impossivel de gravar ate 11/08/2026 (duas CHECK
        // constraints sobrepostas), entao nunca teve case e caia no generico.
        const member = notification.data?.member_name;
        const utilization = notification.data?.utilization;
        if (!member) return renderMessage(t.generic, '');
        return renderMessage(
          t.capacityOverload,
          utilization != null ? `${member} (${Math.round(utilization)}%)` : member
        );
      }
      case 'support_reply':
        return renderMessage(t.supportReply, '');
      case 'support_status_change':
        return renderMessage(t.supportStatusChange, '');
      default:
        return renderMessage(t.generic, '');
    }
  };

  const unreadCount = notifications.filter(n => !n.read_at).length;

  return (
    <div className={fullWidth ? "w-full flex-1 flex flex-col" : "w-80 max-h-96 flex flex-col"}>
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--color-border)]">
        <span className="text-sm font-semibold text-[var(--color-text)]">
          {t.title}
          {unreadCount > 0 && (
            <span className="ml-1.5 text-xs font-normal text-[var(--color-text-muted)]">
              {fill(t.unread, { count: unreadCount })}
            </span>
          )}
        </span>
        {unreadCount > 0 && (
          <button
            onClick={handleMarkAllRead}
            className="flex items-center gap-1 text-xs text-[var(--color-primary)] hover:underline bg-transparent border-none cursor-pointer"
          >
            <CheckCheck size={12} />
            {t.markAll}
          </button>
        )}
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex justify-center py-6">
            <Loader2 size={16} className="animate-spin text-[var(--color-text-muted)]" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="py-8 text-center text-xs text-[var(--color-text-muted)]">
            {t.emptyTitle}
          </div>
        ) : (
          notifications.map(notification => (
            <button
              key={notification.id}
              onClick={() => handleMarkRead(notification)}
              className={`flex items-start gap-2.5 w-full px-3 py-2.5 text-left border-none cursor-pointer transition-colors ${
                notification.read_at
                  ? 'bg-transparent hover:bg-[var(--color-bg-secondary)]'
                  : 'bg-[var(--color-primary)]/5 hover:bg-[var(--color-primary)]/10'
              }`}
            >
              {/* Avatar. actor_id nulo = gerada pelo sistema (alerta de prazo):
                  nao ha pessoa a quem atribuir o aviso, entao entra icone. */}
              {notification.actor_id ? (
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-medium shrink-0 mt-0.5"
                  style={{ backgroundColor: getAvatarColor(notification.actor_id) }}
                >
                  {getInitial(notification.actor?.full_name ?? null)}
                </div>
              ) : (
                <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 bg-[var(--color-bg-secondary)] text-[var(--color-text-muted)]">
                  <CalendarClock size={14} />
                </div>
              )}

              {/* Content */}
              <div className="flex-1 min-w-0">
                <p className="text-xs text-[var(--color-text)] leading-relaxed m-0">
                  {notification.actor_id && (
                    <span className="font-semibold">
                      {notification.actor?.full_name || t.someone}
                    </span>
                  )}
                  {getNotificationMessage(notification)}
                </p>
                {notification.data?.comment_preview && (
                  <p className="text-[11px] text-[var(--color-text-muted)] mt-0.5 truncate m-0">
                    "{notification.data.comment_preview}"
                  </p>
                )}
                <span className="text-[10px] text-[var(--color-text-muted)] mt-0.5 block">
                  {notification.data?.project_name && (
                    <>{notification.data.project_name} · </>
                  )}
                  {formatRelativeTime(notification.created_at)}
                </span>
              </div>

              {/* Unread indicator */}
              {!notification.read_at && (
                <div className="w-2 h-2 rounded-full bg-[var(--color-primary)] shrink-0 mt-2" />
              )}
            </button>
          ))
        )}
      </div>
    </div>
  );
}
